import React, { useState, useEffect, useCallback } from 'react';
import { 
  Search, Users, Star, CheckCircle, XCircle, Clock, 
  ArrowLeft, Filter, FileText, UserCheck, Activity, 
  ShieldAlert, BookOpen, AlertTriangle, Check, X, Briefcase 
} from 'lucide-react';
const GOOGLE_SCRIPT_URL = "https://script.google.com/macros/s/AKfycbxZL9N5rxe6nV1ZGoFHKTf36URSy2PzuBctC4T40A4cbDx8rSfUqXSTgN0Z8KhclQnFcw/exec";

// ─── THEME STYLES (AGGRESSIVE GLOBAL DARK MODE) ─────────────────────────────
const themeStyles = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700;9..40,800&family=DM+Mono:wght@400;500;600&display=swap');

  /* Fallback / Light Mode Variables */
  :root, body, html, .nova-hiring-wrapper {
    --bg-page: #F3F4F6;
    --bg-card: #ffffff;
    --bg-panel: #F9FAFB;
    --bg-panel-alt: #F3F4F6;
    --text-strong: #111827;
    --text-main: #374151;
    --text-secondary: #6B7280;
    --text-muted: #9CA3AF;
    --border-main: #E5E7EB;
    --border-light: #F3F4F6;
    --shadow-sm: 0 1px 3px rgba(0,0,0,0.05);
    --shadow-md: 0 10px 30px rgba(0,0,0,0.08);
    --shadow-hover: 0 4px 20px rgba(0,0,0,0.08);

    --accent-blue: #2563EB;
    --accent-green: #059669;
    --accent-red: #DC2626;
    --accent-amber: #D97706;
    --accent-purple: #7C3AED;
    --accent-cyan: #0891B2;

    /* Status Colors - Light */
    --st-sup-bg: #ede9fe; --st-sup-txt: #6d28d9; --st-sup-border: rgba(124, 58, 237, 0.3); --st-sup-grad: linear-gradient(135deg, #7c3aed 0%, #a78bfa 100%);
    --st-fo-bg: #d1fae5;  --st-fo-txt: #065f46;  --st-fo-border: rgba(5, 150, 105, 0.3);   --st-fo-grad: linear-gradient(135deg, #059669 0%, #34d399 100%);
    --st-rej-bg: #fee2e2; --st-rej-txt: #991b1b; --st-rej-border: rgba(220, 38, 38, 0.3);  --st-rej-grad: linear-gradient(135deg, #dc2626 0%, #f87171 100%);
    --st-pen-bg: #fef3c7; --st-pen-txt: #92400e; --st-pen-border: rgba(217, 119, 6, 0.3);  --st-pen-grad: linear-gradient(135deg, #d97706 0%, #fbbf24 100%);
    
    --yn-y-bg: #d1fae5; --yn-y-txt: #065f46;
    --yn-n-bg: #fee2e2; --yn-n-txt: #991b1b;
  }

  /* Extreme Aggressive Dark Mode Selectors */
  html.dark, html.dark-mode, html[data-theme='dark'], html[data-bs-theme='dark'],
  body.dark, body.dark-mode, body[data-theme='dark'], body[data-bs-theme='dark'],
  .dark, .dark-mode, [data-theme='dark'], [data-bs-theme='dark'],
  .dark .nova-hiring-wrapper, .dark-mode .nova-hiring-wrapper, [data-theme='dark'] .nova-hiring-wrapper, [data-bs-theme='dark'] .nova-hiring-wrapper,
  .nova-hiring-wrapper.dark, .nova-hiring-wrapper[data-theme='dark'] {
    --bg-page: #0b1437 !important;
    --bg-card: #111c44 !important;
    --bg-panel: #0f172a !important;
    --bg-panel-alt: #1e293b !important;
    --text-strong: #ffffff !important;
    --text-main: #e2e8f0 !important;
    --text-secondary: #94a3b8 !important;
    --text-muted: #64748b !important;
    --border-main: #2b3674 !important;
    --border-light: #1e293b !important;
    --shadow-sm: 0 1px 3px rgba(0,0,0,0.4) !important;
    --shadow-md: 0 10px 40px rgba(0,0,0,0.6) !important;
    --shadow-hover: 0 4px 20px rgba(0,0,0,0.6) !important;

    --accent-blue: #38bdf8 !important;
    --accent-green: #34d399 !important;
    --accent-red: #f87171 !important;
    --accent-amber: #fbbf24 !important;
    --accent-purple: #a78bfa !important;
    --accent-cyan: #22d3ee !important;

    /* Status Colors - Dark */
    --st-sup-bg: rgba(124, 58, 237, 0.15) !important; --st-sup-txt: #c4b5fd !important; --st-sup-border: rgba(124, 58, 237, 0.4) !important; 
    --st-fo-bg: rgba(5, 150, 105, 0.15) !important;   --st-fo-txt: #6ee7b7 !important;  --st-fo-border: rgba(5, 150, 105, 0.4) !important;   
    --st-rej-bg: rgba(220, 38, 38, 0.15) !important;  --st-rej-txt: #fca5a5 !important; --st-rej-border: rgba(220, 38, 38, 0.4) !important;  
    --st-pen-bg: rgba(217, 119, 6, 0.15) !important;  --st-pen-txt: #fcd34d !important; --st-pen-border: rgba(217, 119, 6, 0.4) !important;  

    --yn-y-bg: rgba(16, 185, 129, 0.15) !important; --yn-y-txt: #6ee7b7 !important;
    --yn-n-bg: rgba(239, 68, 68, 0.15) !important; --yn-n-txt: #fca5a5 !important;
  }

  .nova-hiring-wrapper {
    background-color: var(--bg-page);
    color: var(--text-main);
    min-height: 100vh;
    font-family: 'DM Sans', sans-serif;
    transition: background-color 0.3s ease, color 0.3s ease;
    padding: 30px 20px;
  }

  .nova-hiring-wrapper * {
    box-sizing: border-box;
    transition: background-color 0.3s ease, border-color 0.3s ease, color 0.3s ease, box-shadow 0.3s ease;
  }

  .nv-card {
    background: var(--bg-card);
    border: 1px solid var(--border-main);
    border-radius: 16px;
    box-shadow: var(--shadow-sm);
  }

  .nv-hover-card:hover {
    border-color: var(--accent-blue);
    transform: translateY(-2px);
    box-shadow: var(--shadow-hover);
  }

  .nv-input {
    width: 100%;
    padding: 10px 14px;
    border-radius: 10px;
    border: 1px solid var(--border-main);
    background: var(--bg-panel);
    color: var(--text-strong);
    font-size: 14px;
    font-family: 'DM Sans', sans-serif;
    outline: none;
  }
  .nv-input:focus { border-color: var(--accent-blue); }
  .nv-input::placeholder { color: var(--text-muted); }

  .nv-btn {
    padding: 10px 20px;
    border-radius: 10px;
    border: none;
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
    font-family: 'DM Sans', sans-serif;
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }
  .nv-btn-primary {
    background: linear-gradient(135deg, #2563EB, #38BDF8);
    color: #ffffff !important;
    box-shadow: 0 4px 12px rgba(37, 99, 235, 0.2);
  }
  .nv-btn-primary:hover { opacity: 0.9; }

  @keyframes spin { to { transform: rotate(360deg); } }
  .nv-spin { animation: spin 0.8s linear infinite; }
  
  ::-webkit-scrollbar { width: 6px; height: 6px; }
  ::-webkit-scrollbar-thumb { background: var(--border-main); border-radius: 4px; }
`;

// ─── DECISION CONFIG ───────────────────────────────────────────────────────────
const getDCfg = (d) => {
  const map = {
    Supervisor:     { label: 'Supervisor', bg: 'var(--st-sup-bg)', txt: 'var(--st-sup-txt)', border: 'var(--st-sup-border)', grad: 'var(--st-sup-grad)', icon: <Star size={16}/> },
    FO:             { label: 'Selected (FO)', bg: 'var(--st-fo-bg)', txt: 'var(--st-fo-txt)', border: 'var(--st-fo-border)', grad: 'var(--st-fo-grad)', icon: <CheckCircle size={16}/> },
    'Not Selected': { label: 'Not Selected', bg: 'var(--st-rej-bg)', txt: 'var(--st-rej-txt)', border: 'var(--st-rej-border)', grad: 'var(--st-rej-grad)', icon: <XCircle size={16}/> },
    Pending:        { label: 'Pending', bg: 'var(--st-pen-bg)', txt: 'var(--st-pen-txt)', border: 'var(--st-pen-border)', grad: 'var(--st-pen-grad)', icon: <Clock size={16}/> },
  };
  return map[d] || map['Pending'];
};

// ─── SHARED COMPONENTS ─────────────────────────────────────────────────────────

const ScoreDisplay = ({ value, max, colorVar }) => {
  const pct = Math.min(100, Math.round((Number(value) / Number(max)) * 100));
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '6px' }}>
      <div style={{ flex: 1, height: '8px', background: 'var(--border-main)', borderRadius: '99px', overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: `var(${colorVar})`, borderRadius: '99px', transition: 'width 0.5s ease-out' }} />
      </div>
      <span style={{ fontWeight: '800', color: `var(${colorVar})`, fontSize: '15px', minWidth: '58px', fontFamily: "'DM Mono', monospace" }}>{value} <span style={{fontSize:'12px', opacity:0.7}}>/ {max}</span></span>
    </div>
  );
};

const YNChip = ({ val }) => {
  if (!val) return <span style={{ color: 'var(--text-muted)', fontSize: '14px' }}>—</span>;
  const isY = String(val).toLowerCase().startsWith('yes');
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '6px',
      padding: '4px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: '700',
      background: isY ? 'var(--yn-y-bg)' : 'var(--yn-n-bg)', 
      color: isY ? 'var(--yn-y-txt)' : 'var(--yn-n-txt)',
      border: `1px solid ${isY ? 'var(--st-fo-border)' : 'var(--st-rej-border)'}`
    }}>
      {isY ? <><Check size={14}/> Yes | ہاں</> : <><X size={14}/> No | نہیں</>}
    </span>
  );
};

const QRow = ({ no, eng, urdu, value, isScore, max, colorVar }) => (
  <div style={{ marginBottom: '16px', paddingBottom: '16px', borderBottom: '1px dashed var(--border-main)' }}>
    <div style={{ fontSize: '14px', fontWeight: '700', color: 'var(--text-strong)', marginBottom: '6px' }}>
      {no}. {eng}
    </div>
    <div style={{ fontSize: '15px', color: 'var(--text-secondary)', marginBottom: '10px', textAlign: 'right', direction: 'rtl', lineHeight: '1.6' }}>
      {urdu}
    </div>
    {isScore
      ? <ScoreDisplay value={value || 0} max={max} colorVar={colorVar} />
      : <YNChip val={value} />
    }
  </div>
);

const TField = ({ label, value, colorVar }) => (
  <div style={{ marginBottom: '16px', paddingBottom: '16px', borderBottom: '1px dashed var(--border-main)' }}>
    <div style={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '8px', fontWeight: '700' }}>{label}</div>
    <div style={{
      display: 'inline-block', padding: '8px 14px', borderRadius: '8px', fontSize: '14px', fontWeight: '700',
      background: colorVar ? `rgba(var(${colorVar}-rgb, 128,128,128), 0.1)` : 'var(--bg-panel-alt)', 
      color: colorVar ? `var(${colorVar})` : 'var(--text-strong)',
      border: `1px solid ${colorVar ? `var(${colorVar})` : 'var(--border-main)'}`
    }}>
      {value || '—'}
    </div>
  </div>
);

// ─── DETAIL VIEW ───────────────────────────────────────────────────────────────

const DetailView = ({ row, onBack }) => {
  const decision = row['Hiring Decision | بھرتی کا فیصلہ'] || 'Pending';
  const dc = getDCfg(decision);
  const name = row['Candidate Name | امیدوار کا نام'] || '—';
  const total = row['Total Score | کل نمبر'] || 0;
  const outOf = row['Out Of | کل ممکنہ نمبر'] || 125;

  const secHeader = (icon, title, urdu, colorVar) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', borderBottom: `2px solid var(${colorVar})`, paddingBottom: '12px', marginBottom: '20px', marginTop: '10px' }}>
      <div style={{ color: `var(${colorVar})` }}>{icon}</div>
      <div style={{ fontSize: '16px', fontWeight: '800', color: 'var(--text-strong)' }}>
        {title} <span style={{ fontSize: '13px', fontWeight: '500', color: 'var(--text-muted)', marginLeft: '6px' }}>| {urdu}</span>
      </div>
    </div>
  );

  return (
    <div style={{ maxWidth: '960px', margin: '0 auto' }}>
      <button onClick={onBack} style={{ background: 'var(--bg-card)', color: 'var(--text-main)', border: '1px solid var(--border-main)', padding: '10px 20px', borderRadius: '10px', cursor: 'pointer', fontWeight: '700', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', boxShadow: 'var(--shadow-sm)' }}>
        <ArrowLeft size={16} /> Back to Directory | واپس جائیں
      </button>

      <div className="nv-card" style={{ overflow: 'hidden', boxShadow: 'var(--shadow-md)' }}>
        
        {/* HEADER */}
        <div style={{ background: dc.grad, padding: '32px 36px', display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap' }}>
          <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: 'rgba(255,255,255,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', flexShrink: 0 }}>
            {React.cloneElement(dc.icon, { size: 32 })}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '24px', fontWeight: '800', color: 'white' }}>{name}</div>
            <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.9)', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '500' }}>
              <UserCheck size={14} /> {row['Position | عہدہ'] || '—'} 
              <span style={{opacity: 0.5}}>|</span> 
              <Clock size={14} /> {row['Date | تاریخ'] || 'No Date'}
            </div>
          </div>
          <div style={{ textAlign: 'right', background: 'rgba(0,0,0,0.15)', padding: '16px 24px', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.2)' }}>
            <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.8)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px', fontWeight: '700' }}>Final Score</div>
            <div style={{ fontSize: '36px', fontWeight: '900', color: 'white', lineHeight: 1, fontFamily: "'DM Mono', monospace" }}>
              {total}<span style={{ fontSize: '18px', opacity: 0.7, fontWeight: '500' }}>/{outOf}</span>
            </div>
            <div style={{ background: 'rgba(255,255,255,0.9)', color: '#000', padding: '4px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: '800', marginTop: '10px', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
              {React.cloneElement(dc.icon, { size: 12 })} {dc.label}
            </div>
          </div>
        </div>

        <div style={{ padding: '36px' }}>

          {/* EMPLOYEE INFO GRID */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '16px', marginBottom: '40px', backgroundColor: 'var(--bg-panel)', padding: '24px', borderRadius: '12px', border: '1px solid var(--border-main)' }}>
            {[
              { l: 'Candidate Name', u: 'امیدوار کا نام', k: 'Candidate Name | امیدوار کا نام' },
              { l: 'Father\'s Name', u: 'والد کا نام', k: "Father's Name | والد کا نام" },
              { l: 'Age', u: 'عمر', k: 'Age | عمر' },
              { l: 'CNIC', u: 'شناختی کارڈ', k: 'CNIC | شناختی کارڈ' },
              { l: 'Contact', u: 'رابطہ نمبر', k: 'Contact No. | رابطہ نمبر' },
              { l: 'Position', u: 'عہدہ', k: 'Position | عہدہ' },
              { l: 'Address', u: 'پتہ', k: 'Address | پتہ' },
              { l: 'Area / Site', u: 'علاقہ / سائٹ', k: 'Area/Site | علاقہ / سائٹ' },
            ].map((f, i) => (
              <div key={i} style={{ background: 'var(--bg-card)', padding: '12px 16px', borderRadius: '8px', border: '1px solid var(--border-light)' }}>
                <div style={{ fontSize: '10px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '4px', fontWeight: '700' }}>{f.l} | {f.u}</div>
                <div style={{ fontSize: '14px', fontWeight: '700', color: 'var(--text-strong)' }}>{row[f.k] || '—'}</div>
              </div>
            ))}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px' }}>
            {/* LEFT COLUMN */}
            <div>
              {/* SECTION 1: TECHNICAL */}
              {secHeader(<Activity size={20}/>, '1. Technical Knowledge', 'تکنیکی علم', '--accent-blue')}
              <TField label="Knowledge Level | تکنیکی علم کی سطح" value={row['Q1. Technical Knowledge Level | تکنیکی علم']} colorVar="--accent-blue" />
              <QRow no="Q1 Score" eng="Technical Score" urdu="تکنیکی نمبر" isScore value={row['Q1. Technical Score (Max 15)']} max={15} colorVar="--accent-blue" />

              {/* SECTION 2: EXPERIENCE */}
              <div style={{ marginTop: '40px' }}>
                {secHeader(<Briefcase size={20}/>, '2. Work Experience', 'کام کا تجربہ', '--accent-purple')}
                <TField label="Experience Level | تجربے کی مدت" value={row['Q2. Work Experience | کام کا تجربہ']} colorVar="--accent-purple" />
                <QRow no="Q2 Score" eng="Experience Score" urdu="تجربے کے نمبر" isScore value={row['Q2. Experience Score (Max 20)']} max={20} colorVar="--accent-purple" />
              </div>

              {/* SECTION 3: QUALIFICATION */}
              <div style={{ marginTop: '40px' }}>
                {secHeader(<BookOpen size={20}/>, '3. Qualification & Extras', 'قابلیت اور اضافی سرٹیفکیٹ', '--accent-cyan')}
                <TField label="Qualification | تعلیمی قابلیت" value={row['Q3. Qualification | تعلیمی قابلیت']} colorVar="--accent-cyan" />
                <QRow no="Q3 Score" eng="Qualification Score" urdu="قابلیت کے نمبر" isScore value={row['Q3. Qualification Score (Max 20)']} max={20} colorVar="--accent-cyan" />
                <div style={{ marginBottom: '16px', paddingBottom: '16px', borderBottom: '1px dashed var(--border-main)' }}>
                  <div style={{ fontSize: '14px', fontWeight: '700', color: 'var(--text-strong)', marginBottom: '6px' }}>Extra Certificates (Solar/Safety) | اضافی سرٹیفکیٹ</div>
                  <div style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '10px', textAlign: 'right', direction: 'rtl' }}>سولر / سیفٹی اضافی سرٹیفکیٹ</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <YNChip val={row['Q3b. Extra Certificates - Solar/Safety | اضافی سرٹیفکیٹ']} />
                    {row['Q3b. Extra Cert Score (Max 5)'] > 0 && (
                      <span style={{ fontSize: '13px', fontWeight: '800', color: 'var(--accent-cyan)', background: 'rgba(8, 145, 178, 0.1)', padding: '4px 10px', borderRadius: '6px' }}>+{row['Q3b. Extra Cert Score (Max 5)']} pts</span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* RIGHT COLUMN */}
            <div>
              {/* SECTION 4: AREA & MOBILITY */}
              {secHeader(<Activity size={20}/>, '4. Area & Mobility', 'علاقہ اور نقل و حرکت', '--accent-green')}
              {[
                { eng: 'Outdoor Work (heat/dust)', urdu: 'بیرونی کام (گرمی / مٹی)', k: 'Q4a. Outdoor Work (heat/dust) | بیرونی کام' },
                { eng: 'Physical Fitness', urdu: 'جسمانی صحت', k: 'Q4b. Physical Fitness | جسمانی صحت' },
                { eng: 'Local Site/Route Knowledge', urdu: 'مقامی راستوں کا علم', k: 'Q4c. Local Site/Route Knowledge | مقامی راستوں کا علم' },
                { eng: 'Willingness to Travel / Night Duty', urdu: 'سفر / رات کی ڈیوٹی', k: 'Q4d. Willingness to Travel/Night Duty | سفر / رات کی ڈیوٹی' },
                { eng: 'Read & Write Urdu', urdu: 'اردو لکھنا اور پڑھنا', k: 'Q4e. Read & Write Urdu | اردو لکھنا پڑھنا' },
                { eng: 'Read & Write English', urdu: 'انگریزی لکھنا اور پڑھنا', k: 'Q4f. Read & Write English | انگریزی لکھنا پڑھنا' },
                { eng: 'Can Drive a Bike', urdu: 'بائیک چلا سکتا ہے', k: 'Q4g. Can Drive a Bike | بائیک چلا سکتا ہے' },
              ].map((q, i) => (
                <div key={i} style={{ marginBottom: '16px', paddingBottom: '16px', borderBottom: '1px dashed var(--border-main)' }}>
                  <div style={{ fontSize: '14px', fontWeight: '700', color: 'var(--text-strong)', marginBottom: '6px' }}>Q4{String.fromCharCode(97+i)}) {q.eng}</div>
                  <div style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '10px', textAlign: 'right', direction: 'rtl' }}>{q.urdu}</div>
                  <YNChip val={row[q.k]} />
                </div>
              ))}
              <QRow no="Q4 Score" eng="Area & Mobility Score" urdu="علاقہ اور نقل و حرکت کے نمبر" isScore value={row['Q4. Area & Mobility Score (Max 45)']} max={45} colorVar="--accent-green" />

              {/* SECTION 5: SAFETY */}
              <div style={{ marginTop: '40px' }}>
                {secHeader(<ShieldAlert size={20}/>, '5. Safety & Discipline', 'حفاظت، نظم و ضبط', '--accent-red')}
                {[
                  { eng: 'Use of PPE', urdu: 'حفاظتی سامان کا استعمال', k: 'Q5a. Use of PPE | حفاظتی سامان کا استعمال' },
                  { eng: 'Electrical & Height Safety', urdu: 'بجلی اور اونچائی کی حفاظت', k: 'Q5b. Electrical & Height Safety | بجلی اور اونچائی' },
                  { eng: 'Discipline & SOP Compliance', urdu: 'نظم و ضبط اور ایس او پی', k: 'Q5c. Discipline & SOP Compliance | نظم و ضبط' },
                  { eng: 'Smartphone / Laptop Use', urdu: 'اسمارٹ فون / لیپ ٹاپ کا استعمال', k: 'Q5d. Smartphone / Laptop Use | ڈیجیٹل مہارت' },
                ].map((q, i) => (
                  <div key={i} style={{ marginBottom: '16px', paddingBottom: '16px', borderBottom: '1px dashed var(--border-main)' }}>
                    <div style={{ fontSize: '14px', fontWeight: '700', color: 'var(--text-strong)', marginBottom: '6px' }}>Q5{String.fromCharCode(97+i)}) {q.eng}</div>
                    <div style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '10px', textAlign: 'right', direction: 'rtl' }}>{q.urdu}</div>
                    <YNChip val={row[q.k]} />
                  </div>
                ))}
                <QRow no="Q5 Score" eng="Safety & Discipline Score" urdu="حفاظت اور نظم و ضبط کے نمبر" isScore value={row['Q5. Safety & Discipline Score (Max 20)']} max={20} colorVar="--accent-red" />
              </div>
            </div>
          </div>

          {/* EVALUATOR & REMARKS */}
          <div style={{ marginTop: '40px', background: 'var(--bg-panel)', border: '1px solid var(--border-main)', borderRadius: '12px', padding: '24px' }}>
            <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px', fontWeight: '700' }}>Remarks | ریمارکس</div>
                <div style={{ fontSize: '15px', color: 'var(--text-strong)', fontStyle: 'italic', lineHeight: '1.6', background: 'var(--bg-card)', padding: '16px', borderRadius: '8px', border: '1px solid var(--border-light)' }}>
                  "{row['Remarks | ریمارکس'] || 'No remarks provided.'}"
                </div>
              </div>
              <div style={{ minWidth: '250px' }}>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px', fontWeight: '700' }}>Evaluation Details</div>
                <div style={{ background: 'var(--bg-card)', padding: '16px', borderRadius: '8px', border: '1px solid var(--border-light)' }}>
                  <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '8px' }}>
                    Evaluated by: <br/><span style={{ color: 'var(--text-strong)', fontWeight: '800', fontSize: '15px' }}>{row['Evaluator Name | جائزہ لینے والے کا نام'] || '—'}</span>
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                    Submitted At: <br/><span style={{ color: 'var(--text-secondary)', fontWeight: '600' }}>{row['Submitted At | جمع کرانے کا وقت'] || '—'}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

// ─── MAIN COMPONENT ────────────────────────────────────────────────────────────

const HiringDashboard = () => {
  // Global Theme Observer
  const [isDark, setIsDark] = useState(() => {
    if (typeof document !== 'undefined') {
      return document.documentElement.classList.contains('dark') ||
             document.body.classList.contains('dark') ||
             document.documentElement.getAttribute('data-theme') === 'dark' ||
             document.body.getAttribute('data-theme') === 'dark';
    }
    return false;
  });

  useEffect(() => {
    const checkTheme = () => {
      setIsDark(
        document.documentElement.classList.contains('dark') ||
        document.body.classList.contains('dark') ||
        document.documentElement.getAttribute('data-theme') === 'dark' ||
        document.body.getAttribute('data-theme') === 'dark' ||
        document.body.classList.contains('dark-mode')
      );
    };

    checkTheme();
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class', 'data-theme'] });
    observer.observe(document.body, { attributes: true, attributeFilter: ['class', 'data-theme'] });
    return () => observer.disconnect();
  }, []);

  const [view, setView] = useState('list');
  const [selected, setSelected] = useState(null);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('All');
  const [search, setSearch] = useState('');

  const fetchData = async () => {
    setLoading(true); setError(null);
    try {
      const res = await fetch(GOOGLE_SCRIPT_URL);
      const json = await res.json();
      if (json.error) throw new Error(json.error);
      const arr = Array.isArray(json) ? json : (json.data || []);
      setData([...arr].reverse());
    } catch (e) {
      setError(e.message || 'Data load nahi ho saka.');
    }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  if (view === 'detail') return (
    <div className="nova-hiring-wrapper">
      <style>{themeStyles}</style>
      <DetailView row={selected} onBack={() => { setSelected(null); setView('list'); }} />
    </div>
  );

  const counts = {
    Supervisor: data.filter(r => r['Hiring Decision | بھرتی کا فیصلہ'] === 'Supervisor').length,
    FO: data.filter(r => r['Hiring Decision | بھرتی کا فیصلہ'] === 'FO').length,
    'Not Selected': data.filter(r => r['Hiring Decision | بھرتی کا فیصلہ'] === 'Not Selected').length,
  };

  const filtered = data.filter(row => {
    const d = row['Hiring Decision | بھرتی کا فیصلہ'] || 'Pending';
    const matchF = filter === 'All' || d === filter;
    const n = (row['Candidate Name | امیدوار کا نام'] || '').toLowerCase();
    const p = (row['Position | عہدہ'] || '').toLowerCase();
    return matchF && (!search || n.includes(search.toLowerCase()) || p.includes(search.toLowerCase()));
  });

  return (
    <div className="nova-hiring-wrapper">
      <style>{themeStyles}</style>
      <div style={{ maxWidth: '1100px', margin: '0 auto' }}>

        {/* PAGE HEADER */}
        <div style={{ marginBottom: '28px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <div style={{ fontSize: '12px', fontWeight: '800', color: 'var(--accent-blue)', letterSpacing: '0.1em', marginBottom: '4px', textTransform: 'uppercase' }}>Human Resources</div>
            <h1 style={{ fontSize: '28px', fontWeight: '800', color: 'var(--text-strong)', margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Users size={28} color="var(--accent-green)" /> Hiring Applications
            </h1>
            <p style={{ color: 'var(--text-secondary)', marginTop: '8px', fontSize: '14px' }}>
              Review candidates and interview evaluations directly from the field.
            </p>
          </div>
          <button className="nv-btn nv-btn-primary" onClick={fetchData} disabled={loading}>
            {loading ? <><span className="nv-spin" style={{ width: 16, height: 16, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', display: 'inline-block' }} /> Syncing...</> : 'Sync Records'}
          </button>
        </div>

        {/* STAT CARDS */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '28px' }}>
          {[
            { label: 'TOTAL CANDIDATES', count: data.length, icon: <Users size={20}/>, color: 'var(--accent-blue)' },
            { label: 'SUPERVISOR POOL', count: counts.Supervisor, icon: <Star size={20}/>, color: 'var(--accent-purple)' },
            { label: 'FO SELECTED', count: counts.FO, icon: <CheckCircle size={20}/>, color: 'var(--accent-green)' },
            { label: 'NOT SELECTED', count: counts['Not Selected'], icon: <XCircle size={20}/>, color: 'var(--accent-red)' },
          ].map((s, i) => (
            <div key={i} className="nv-card nv-hover-card" style={{ padding: '20px 24px', borderTop: `3px solid ${s.color}`, cursor: 'pointer' }} onClick={() => setFilter(s.label.includes('TOTAL') ? 'All' : s.label.includes('SUPERVISOR') ? 'Supervisor' : s.label.includes('FO') ? 'FO' : 'Not Selected')}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '11px', fontWeight: '700', letterSpacing: '0.1em', color: 'var(--text-muted)', marginBottom: '12px' }}>
                <span style={{ color: s.color }}>{s.icon}</span> {s.label}
              </div>
              <div style={{ fontSize: '36px', fontWeight: '800', color: 'var(--text-strong)', lineHeight: 1, fontFamily: "'DM Mono', monospace" }}>{s.count}</div>
            </div>
          ))}
        </div>

        {/* SEARCH + FILTER */}
        <div className="nv-card" style={{ padding: '16px 20px', marginBottom: '24px', display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ position: 'relative', flex: 1, minWidth: '260px', maxWidth: '400px' }}>
            <Search size={16} color="var(--text-muted)" style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)' }} />
            <input 
              className="nv-input" 
              style={{ paddingLeft: '40px' }}
              value={search} 
              onChange={e => setSearch(e.target.value)} 
              placeholder="Search by candidate name or position..."
            />
          </div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {[
              { val: 'All', label: `All`, color: 'var(--accent-blue)', bg: 'rgba(37,99,235,0.1)' },
              { val: 'Supervisor', label: `Supervisor`, color: 'var(--accent-purple)', bg: 'rgba(124,58,237,0.1)' },
              { val: 'FO', label: `FO Selected`, color: 'var(--accent-green)', bg: 'rgba(5,150,105,0.1)' },
              { val: 'Not Selected', label: `Not Selected`, color: 'var(--accent-red)', bg: 'rgba(220,38,38,0.1)' },
            ].map(btn => (
              <button key={btn.val} onClick={() => setFilter(btn.val)} style={{
                padding: '8px 16px', borderRadius: '8px', fontSize: '13px', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", transition: 'all 0.2s',
                border: `1px solid ${filter === btn.val ? btn.color : 'var(--border-main)'}`,
                background: filter === btn.val ? btn.bg : 'var(--bg-card)',
                color: filter === btn.val ? btn.color : 'var(--text-secondary)',
                fontWeight: filter === btn.val ? '700' : '600',
              }}>{btn.label}</button>
            ))}
          </div>
        </div>

        {/* ERROR */}
        {error && (
          <div style={{ background: 'var(--st-rej-bg)', border: '1px solid var(--st-rej-border)', borderRadius: '12px', padding: '16px 20px', marginBottom: '24px', color: 'var(--st-rej-txt)', fontSize: '14px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <AlertTriangle size={18} /> {error}
          </div>
        )}

        {/* TABLE */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '80px 20px', color: 'var(--text-muted)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
            <div className="nv-spin" style={{ width: 36, height: 36, border: '3px solid var(--border-main)', borderTopColor: 'var(--accent-blue)', borderRadius: '50%' }} />
            <div style={{ fontSize: '14px', fontWeight: '700', letterSpacing: '0.05em' }}>FETCHING APPLICATIONS...</div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="nv-card" style={{ textAlign: 'center', padding: '80px 20px', color: 'var(--text-muted)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
            <Users size={48} color="var(--border-main)" />
            <div style={{ fontSize: '16px', fontWeight: '700', color: 'var(--text-main)' }}>No candidates found</div>
            <div style={{ fontSize: '13px' }}>Try adjusting your search or filter.</div>
          </div>
        ) : (
          <div className="nv-card" style={{ overflow: 'hidden' }}>
            {/* Table Header */}
            <div style={{ display: 'grid', gridTemplateColumns: '0.5fr 2.5fr 1.5fr 1.2fr 1.5fr 1fr', padding: '16px 24px', background: 'var(--bg-panel)', borderBottom: '1px solid var(--border-main)', fontWeight: '700', fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
              <div>#</div>
              <div>Candidate Info</div>
              <div>Position</div>
              <div>Score</div>
              <div>Decision</div>
              <div>Action</div>
            </div>

            {/* Rows */}
            {filtered.map((row, i) => {
              const d = row['Hiring Decision | بھرتی کا فیصلہ'] || 'Pending';
              const dc = getDCfg(d);
              const total = row['Total Score | کل نمبر'] || 0;
              return (
                <div
                  key={i}
                  onClick={() => { setSelected(row); setView('detail'); }}
                  className="cp-job-row"
                  style={{ display: 'grid', gridTemplateColumns: '0.5fr 2.5fr 1.5fr 1.2fr 1.5fr 1fr', padding: '16px 24px', borderBottom: '1px solid var(--border-light)', alignItems: 'center', cursor: 'pointer', transition: 'background 0.2s', fontSize: '14px', color: 'var(--text-main)' }}
                  onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--bg-panel-alt)'}
                  onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <div style={{ color: 'var(--text-muted)', fontWeight: '700', fontFamily: "'DM Mono', monospace" }}>{filtered.length - i}</div>
                  
                  <div>
                    <div style={{ fontWeight: '800', color: 'var(--text-strong)', marginBottom: '4px', fontSize: '15px' }}>{row['Candidate Name | امیدوار کا نام'] || '—'}</div>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Clock size={11} /> {row['Date | تاریخ'] || '—'}
                    </div>
                  </div>
                  
                  <div>
                    <span style={{ background: 'var(--bg-panel)', border: '1px solid var(--border-main)', color: 'var(--text-strong)', padding: '4px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: '700' }}>
                      {row['Position | عہدہ'] || '—'}
                    </span>
                  </div>
                  
                  <div style={{ fontWeight: '800', color: 'var(--text-strong)', fontSize: '16px', fontFamily: "'DM Mono', monospace" }}>
                    {total}<span style={{ fontSize: '12px', color: 'var(--text-muted)', fontWeight: '500' }}>/125</span>
                  </div>
                  
                  <div>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: dc.bg, color: dc.txt, padding: '4px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: '800', border: `1px solid ${dc.border}` }}>
                      {React.cloneElement(dc.icon, { size: 14 })} {dc.label}
                    </span>
                  </div>
                  
                  <div>
                    <button style={{ backgroundColor: 'var(--bg-page)', color: 'var(--text-strong)', border: '1px solid var(--border-main)', padding: '6px 14px', borderRadius: '8px', fontSize: '12px', fontWeight: '700', cursor: 'pointer', transition: 'all 0.2s' }}>
                      Review
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default HiringDashboard;